import React, { useState } from 'react';
import { FileText, CheckCircle, XCircle, Clock, AlertTriangle, Filter } from 'lucide-react';

const ProcessingLogs = () => {
  const [filterLevel, setFilterLevel] = useState('all');
  const [logs, setLogs] = useState([
    {
      id: 1,
      timestamp: '2026-02-04T03:15:23Z',
      level: 'success',
      batch: 'Daily Settlement Reconciliation',
      message: 'Batch processing completed successfully',
      details: 'Processed 1,234 transactions in 12m 34s',
      duration: '12m 34s'
    },
    {
      id: 2,
      timestamp: '2026-02-04T03:14:45Z',
      level: 'info',
      batch: 'Daily Settlement Reconciliation',
      message: 'Anomaly detection scan initiated',
      details: 'Scanning 1,234 transactions for irregularities',
      duration: '38s'
    },
    {
      id: 3,
      timestamp: '2026-02-04T03:12:10Z',
      level: 'warning',
      batch: 'Hourly Quick Reconciliation',
      message: 'Minor discrepancy detected and auto-resolved',
      details: 'Amount variance of $15.50 matched with pending transaction',
      duration: '3m 21s'
    },
    {
      id: 4,
      timestamp: '2026-02-04T03:00:00Z',
      level: 'success',
      batch: 'Hourly Quick Reconciliation',
      message: 'Hourly batch completed',
      details: 'Processed 156 transactions with 100% success rate',
      duration: '3m 21s'
    },
    {
      id: 5,
      timestamp: '2026-02-04T02:45:30Z',
      level: 'error',
      batch: 'Daily Settlement Reconciliation',
      message: 'Gateway timeout during settlement fetch',
      details: 'Retrying with exponential backoff (Attempt 2/3)',
      duration: '5s'
    },
    {
      id: 6,
      timestamp: '2026-02-04T02:30:15Z',
      level: 'info',
      batch: 'Daily Settlement Reconciliation',
      message: 'Starting scheduled batch processing',
      details: 'Fetching settlement data from 3 payment gateways',
      duration: '0s'
    }
  ]);

  const getLevelIcon = (level) => {
    switch (level) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-orange-600" />;
      case 'info':
        return <Clock className="w-5 h-5 text-blue-600" />;
      default:
        return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-orange-50 border-orange-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const filteredLogs = filterLevel === 'all'
    ? logs
    : logs?.filter(log => log?.level === filterLevel);

  return (
    <div className="space-y-4">
      {/* Filter Controls */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Real-Time Processing Logs
        </h3>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e?.target?.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">All Levels</option>
            <option value="success">Success</option>
            <option value="info">Info</option>
            <option value="warning">Warning</option>
            <option value="error">Error</option>
          </select>
        </div>
      </div>
      {/* Log Entries */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto">
        {filteredLogs?.map((log) => (
          <div
            key={log?.id}
            className={`border rounded-lg p-4 ${getLevelColor(log?.level)}`}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                {getLevelIcon(log?.level)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 mb-1">
                      {log?.message}
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      {log?.details}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 ml-4 whitespace-nowrap">
                    {new Date(log.timestamp)?.toLocaleTimeString()}
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-600">
                  <span className="font-medium">{log?.batch}</span>
                  <span>Duration: {log?.duration}</span>
                  <span className="capitalize px-2 py-0.5 bg-white rounded border border-gray-200">
                    {log?.level}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {filteredLogs?.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No logs found for selected filter</p>
        </div>
      )}
    </div>
  );
};

export default ProcessingLogs;